import type {
  DocPackCatalog,
  DocPackCatalogPage,
  DocPackClassCount,
  DocPackPage,
  DocPackPageCharRange,
  DocPackPageSegment,
  DocPackReadingClass,
} from '@listening/doc-pack';
import { glueDocPackRangeTexts } from '@listening/doc-pack';

export type DocPackReadingClassOverrides = Partial<
  Record<DocPackReadingClass, boolean>
>;

export type DocPackReadingAnchor = {
  readonly page: number;
  readonly charStart: number;
};

export type DocPackReadingPlanRange = {
  readonly page: number;
  readonly class: DocPackReadingClass;
  readonly chars: readonly [number, number];
  readonly text: string;
};

export type DocPackReadingParagraph = {
  readonly page: number;
  readonly class: DocPackReadingClass;
  readonly chars: readonly [number, number];
  readonly text: string;
};

export type DocPackReadingChunk = {
  readonly id: string;
  readonly class: DocPackReadingClass;
  readonly ranges: readonly DocPackPageCharRange[];
  readonly text: string;
  readonly charCount: number;
  readonly wordCount: number;
};

export type BuildDocPackReadingPlanOptions = {
  readonly classOverrides?: DocPackReadingClassOverrides | undefined;
  readonly targetWordsPerChunk?: number | undefined;
  readonly maxWordsPerChunk?: number | undefined;
  readonly splitAtSkippedSegments?: boolean | undefined;
};

const DEFAULT_TARGET_WORDS_PER_CHUNK = 95;
// Hard cap on a synthesis chunk, and the default cap on a rendered range —
// buildDocPackReadingRanges has no window options and takes it as-is.
const DEFAULT_MAX_WORDS_PER_CHUNK = 140;
const DEFAULT_ESTIMATED_WORD_MS = 380;

export const isDocPackClassEnabled = (
  segment: Pick<DocPackPageSegment, 'class' | 'read'>,
  classOverrides?: DocPackReadingClassOverrides,
) => classOverrides?.[segment.class] ?? segment.read;

const countWords = (text: string) => text.match(/\S+/g)?.length ?? 0;

const rangeIdPart = (range: DocPackPageCharRange) =>
  `${range[0].toString()}:${range[1].toString()}-${range[2].toString()}`;

const chunkId = (
  cls: DocPackReadingClass,
  ranges: readonly DocPackPageCharRange[],
) => `${cls}:${ranges.map(rangeIdPart).join(',')}`;

const rangeText = (page: DocPackPage, chars: readonly [number, number]) =>
  page.text.slice(chars[0], chars[1]);

const intersectRanges = (
  a: readonly [number, number],
  b: readonly [number, number],
): readonly [number, number] | null => {
  const start = Math.max(a[0], b[0]);
  const end = Math.min(a[1], b[1]);
  return start < end ? [start, end] : null;
};

/**
 * Word-aligned hard cap for one range. A sentence longer than `maxWords` is cut
 * at the START of its (maxWords + 1)-th word, so the parts stay contiguous (no
 * gap, no overlap, concatenation equals the original range) and no word
 * straddles a boundary — word provenance offsets and rendered text are
 * unchanged by the split. Without it a single oversized sentence is an
 * unbounded unit: one synthesis chunk over the max, and one rendered <Text>
 * that re-shapes in full on every word advance.
 */
const splitRangeAtWordCap = (
  page: DocPackPage,
  chars: readonly [number, number],
  maxWords: number,
): ReadonlyArray<readonly [number, number]> => {
  if (maxWords < 1) return [chars];
  const wordStarts = [
    ...page.text.slice(chars[0], chars[1]).matchAll(/\S+/g),
  ].map((match) => chars[0] + match.index);
  if (wordStarts.length <= maxWords) return [chars];

  const parts: Array<readonly [number, number]> = [];
  let start = chars[0];
  for (let index = maxWords; index < wordStarts.length; index += maxWords) {
    const cut = wordStarts[index];
    if (cut === undefined) break;
    parts.push([start, cut]);
    start = cut;
  }
  parts.push([start, chars[1]]);
  return parts;
};

const sentenceRangesForSegment = (
  page: DocPackPage,
  segment: Pick<DocPackPageSegment, 'chars'>,
  maxWords = DEFAULT_MAX_WORDS_PER_CHUNK,
): ReadonlyArray<readonly [number, number]> => {
  const sentenceRanges = page.sentences
    .map((sentence) => intersectRanges(segment.chars, sentence.chars))
    .filter((range): range is readonly [number, number] => range !== null);
  return (sentenceRanges.length > 0 ? sentenceRanges : [segment.chars]).flatMap(
    (chars) => splitRangeAtWordCap(page, chars, maxWords),
  );
};

const sameBlock = (
  a: DocPackPageSegment | undefined,
  b: DocPackPageSegment | undefined,
) => a?.blockId !== undefined && a.blockId === b?.blockId;

/**
 * The enabled span of each block on the page, in order: a paragraph whose inline citation is a
 * segment of its own is still one span, from its first enabled segment to its last, so it renders
 * as one paragraph. A skipped inline segment between enabled ones stays visible, unread.
 */
const enabledBlockSpans = (
  page: DocPackPage,
  classOverrides?: DocPackReadingClassOverrides,
) => {
  const spans: {
    class: DocPackReadingClass;
    chars: [number, number];
    reads: [number, number][];
  }[] = [];
  let lastEnabled: DocPackPageSegment | undefined;
  let skippedSinceEnabled = false;
  for (const segment of page.segments) {
    if (!isDocPackClassEnabled(segment, classOverrides)) {
      skippedSinceEnabled = true;
      continue;
    }
    const span = spans.at(-1);
    if (span && sameBlock(lastEnabled, segment)) {
      span.chars[1] = segment.chars[1];
      const lastRead = span.reads.at(-1);
      if (lastRead && !skippedSinceEnabled) lastRead[1] = segment.chars[1];
      else span.reads.push([segment.chars[0], segment.chars[1]]);
    } else
      spans.push({
        class: segment.class,
        chars: [segment.chars[0], segment.chars[1]],
        reads: [[segment.chars[0], segment.chars[1]]],
      });
    lastEnabled = segment;
    skippedSinceEnabled = false;
  }
  return spans;
};

export function buildDocPackReadingRanges(
  pages: readonly DocPackPage[],
  classOverrides?: DocPackReadingClassOverrides,
): readonly DocPackReadingPlanRange[] {
  return pages
    .toSorted((a, b) => a.page - b.page)
    .flatMap((page) =>
      enabledBlockSpans(page, classOverrides).flatMap((span) =>
        span.reads.flatMap((read) =>
          sentenceRangesForSegment(page, { chars: read }).map((chars) => ({
            page: page.page,
            class: span.class,
            chars,
            text: rangeText(page, chars),
          })),
        ),
      ),
    )
    .filter((range) => range.text.trim().length > 0);
}

export function buildDocPackReadingParagraphs(
  pages: readonly DocPackPage[],
  classOverrides?: DocPackReadingClassOverrides,
): readonly DocPackReadingParagraph[] {
  return pages
    .toSorted((a, b) => a.page - b.page)
    .flatMap((page) =>
      enabledBlockSpans(page, classOverrides).map((span) => ({
        page: page.page,
        class: span.class,
        chars: span.chars,
        text: rangeText(page, span.chars),
      })),
    )
    .filter((paragraph) => paragraph.text.trim().length > 0);
}

export const DOC_PACK_READING_CHUNKS_VERSION = 2;

export function buildDocPackReadingChunks(
  pages: readonly DocPackPage[],
  options: BuildDocPackReadingPlanOptions = {},
): readonly DocPackReadingChunk[] {
  const targetWords =
    options.targetWordsPerChunk ?? DEFAULT_TARGET_WORDS_PER_CHUNK;
  const maxWords = options.maxWordsPerChunk ?? DEFAULT_MAX_WORDS_PER_CHUNK;
  const chunks: DocPackReadingChunk[] = [];
  let currentClass: DocPackReadingClass | undefined;
  let currentRanges: DocPackPageCharRange[] = [];
  let currentTexts: string[] = [];
  let currentWordCount = 0;

  const flush = () => {
    if (!currentClass || currentRanges.length === 0) return;
    const text = glueDocPackRangeTexts(currentTexts);
    chunks.push({
      id: chunkId(currentClass, currentRanges),
      class: currentClass,
      ranges: [...currentRanges],
      text,
      charCount: text.length,
      wordCount: currentWordCount,
    });
    currentClass = undefined;
    currentRanges = [];
    currentTexts = [];
    currentWordCount = 0;
  };

  for (const page of pages.toSorted((a, b) => a.page - b.page)) {
    // Per-page snap: a chunk never spans pages, so chunk ids are deterministic
    // and page-addressable — a click re-seeks WITHIN a stable chunk, it never
    // re-chunks the doc relative to the click.
    flush();
    let lastEnabled: DocPackPageSegment | undefined;
    page.segments.forEach((segment, index) => {
      if (!isDocPackClassEnabled(segment, options.classOverrides)) {
        // A skipped citation inside an otherwise-read block leaves the
        // sentence around it in one chunk.
        const readInBlock = (step: 1 | -1) => {
          for (let i = index + step; ; i += step) {
            const neighbour = page.segments[i];
            if (neighbour === undefined || !sameBlock(neighbour, segment))
              return false;
            if (isDocPackClassEnabled(neighbour, options.classOverrides))
              return true;
            if (neighbour.class !== 'citation') return false;
          }
        };
        const inline =
          !options.splitAtSkippedSegments &&
          segment.class === 'citation' &&
          (readInBlock(-1) || readInBlock(1));
        if (!inline) flush();
        return;
      }
      const continuesBlock =
        !options.splitAtSkippedSegments && sameBlock(lastEnabled, segment);
      lastEnabled = segment;

      for (const segmentChars of sentenceRangesForSegment(
        page,
        segment,
        maxWords,
      )) {
        const text = rangeText(page, segmentChars);
        if (text.trim().length === 0) continue;
        const wordCount = countWords(text);
        const nextWouldExceedMax =
          currentRanges.length > 0 && currentWordCount + wordCount > maxWords;
        const nextWouldExceedTarget =
          currentRanges.length > 0 &&
          currentWordCount >= targetWords &&
          currentClass === segment.class;

        if (
          currentClass !== undefined &&
          ((currentClass !== segment.class && !continuesBlock) ||
            nextWouldExceedMax ||
            nextWouldExceedTarget)
        ) {
          flush();
        }

        if (currentClass === undefined || currentClass === 'citation')
          currentClass = segment.class;
        currentRanges.push([page.page, segmentChars[0], segmentChars[1]]);
        currentTexts.push(text);
        currentWordCount += wordCount;
      }
    });
  }

  flush();
  return chunks;
}

/**
 * Find the STABLE chunk to play for an anchor (a clicked word, or the end of the
 * previous chunk when advancing). Returns the chunk whose ranges contain the
 * anchor; if none does (the anchor is in a skipped gap, or sits exactly on a
 * chunk boundary while advancing), the first chunk that starts at/after the
 * anchor in reading order. Chunks are already emitted in reading order, so no
 * sort is needed.
 */
export function findDocPackChunkContaining(
  chunks: readonly DocPackReadingChunk[],
  anchor: DocPackReadingAnchor,
): DocPackReadingChunk | undefined {
  for (const chunk of chunks) {
    for (const [page, start, end] of chunk.ranges) {
      if (
        page === anchor.page &&
        start <= anchor.charStart &&
        anchor.charStart < end
      ) {
        return chunk;
      }
    }
  }
  for (const chunk of chunks) {
    const first = chunk.ranges[0];
    const last = chunk.ranges.at(-1);
    if (
      first &&
      last &&
      first[0] === anchor.page &&
      first[1] <= anchor.charStart &&
      anchor.charStart < last[2]
    )
      return chunk;
  }
  for (const chunk of chunks) {
    const first = chunk.ranges[0];
    if (!first) continue;
    const [page, start] = first;
    if (
      page > anchor.page ||
      (page === anchor.page && start >= anchor.charStart)
    )
      return chunk;
  }
  return undefined;
}

export function resolveDocPackReadingChunkText(
  pages: readonly DocPackPage[],
  ranges: readonly DocPackPageCharRange[],
) {
  const pageByNumber = new Map(pages.map((page) => [page.page, page]));
  return glueDocPackRangeTexts(
    ranges.map(([pageNumber, start, end]) => {
      const page = pageByNumber.get(pageNumber);
      if (!page) return '';
      return page.text.slice(start, end);
    }),
  );
}

/**
 * Catalog-count mirror of {@link isDocPackClassEnabled}: `counts.chars`/`words`
 * aggregate a class's read:true segments (played when the class has no
 * override) and `offChars`/`offWords` its read:false segments (played only
 * when the class is overridden on). Class DEFAULTS play no role here — the
 * builder already resolved them into each segment's `read` flag, and a
 * segment's flag can disagree with its class default (a dropped small-font
 * body section is `{class:'body', read:false}`). Missing off buckets decode
 * as 0, so pre-split catalogs keep their previous all-in-`chars` totals.
 */
const enabledCountsForClass = (
  counts: DocPackClassCount,
  override: boolean | undefined,
): { chars: number; words: number } => {
  if (override === false) return { chars: 0, words: 0 };
  if (override === true) {
    return {
      chars: counts.chars + (counts.offChars ?? 0),
      words: counts.words + (counts.offWords ?? 0),
    };
  }
  return { chars: counts.chars, words: counts.words };
};

export function docPackCatalogPageReadableCounts(
  page: DocPackCatalogPage,
  classOverrides?: DocPackReadingClassOverrides,
) {
  return Object.entries(page.classes).reduce(
    (acc, [cls, counts]) => {
      if (counts === undefined) return acc;
      const enabled = enabledCountsForClass(
        counts,
        classOverrides?.[cls as DocPackReadingClass],
      );
      return {
        chars: acc.chars + enabled.chars,
        words: acc.words + enabled.words,
      };
    },
    { chars: 0, words: 0 },
  );
}

export function estimateDocPackReadingDurationMs(
  catalog: Pick<DocPackCatalog, 'pages'>,
  classOverrides?: DocPackReadingClassOverrides,
  estimatedWordMs = DEFAULT_ESTIMATED_WORD_MS,
) {
  return catalog.pages.reduce((total, page) => {
    const counts = docPackCatalogPageReadableCounts(page, classOverrides);
    return total + counts.words * estimatedWordMs;
  }, 0);
}

// Total chars on the page regardless of read state — the off buckets are part
// of the page text, so they belong in any full-text denominator.
export const catalogPageTotalChars = (page: DocPackCatalogPage) =>
  Object.values(page.classes).reduce(
    (sum, counts) => sum + (counts?.chars ?? 0) + (counts?.offChars ?? 0),
    0,
  );

export function docPackDocumentPositionMsForAnchor(
  catalog: Pick<DocPackCatalog, 'pages'>,
  anchor: DocPackReadingAnchor,
  classOverrides?: DocPackReadingClassOverrides,
  estimatedWordMs = DEFAULT_ESTIMATED_WORD_MS,
) {
  let elapsed = 0;
  for (const page of catalog.pages) {
    const counts = docPackCatalogPageReadableCounts(page, classOverrides);
    const pageDuration = counts.words * estimatedWordMs;
    if (page.n !== anchor.page) {
      elapsed += pageDuration;
      continue;
    }
    // anchor.charStart is an ABSOLUTE offset into the full page text (readable
    // + skipped), so divide by the page's total char count, not the
    // readable-only subset — otherwise pages with skipped content overshoot and
    // clamp to 1. This catalog-only estimate is refined once the page loads via
    // docPackDocumentPositionMsForLoadedPageAnchor, which uses the exact readable
    // offset.
    const totalChars = catalogPageTotalChars(page);
    if (totalChars === 0) return elapsed;
    const ratio = Math.min(1, Math.max(0, anchor.charStart / totalChars));
    return elapsed + pageDuration * ratio;
  }
  return elapsed;
}

export function docPackDocumentPositionMsForLoadedPageAnchor(
  catalog: Pick<DocPackCatalog, 'pages'>,
  page: DocPackPage,
  anchor: DocPackReadingAnchor,
  classOverrides?: DocPackReadingClassOverrides,
  estimatedWordMs = DEFAULT_ESTIMATED_WORD_MS,
) {
  let elapsed = 0;
  for (const catalogPage of catalog.pages) {
    const counts = docPackCatalogPageReadableCounts(
      catalogPage,
      classOverrides,
    );
    const pageDuration = counts.words * estimatedWordMs;
    if (catalogPage.n !== anchor.page) {
      elapsed += pageDuration;
      continue;
    }
    if (counts.chars === 0) return elapsed;
    const readableOffset = docPackPageReadableCharOffsetForAnchor(
      page,
      anchor,
      classOverrides,
    );
    const ratio = Math.min(1, Math.max(0, readableOffset / counts.chars));
    return elapsed + pageDuration * ratio;
  }
  return elapsed;
}

export function docPackPageReadableCharOffsetForAnchor(
  page: DocPackPage,
  anchor: DocPackReadingAnchor,
  classOverrides?: DocPackReadingClassOverrides,
) {
  if (anchor.page !== page.page) return 0;
  let offset = 0;
  for (const segment of page.segments) {
    if (!isDocPackClassEnabled(segment, classOverrides)) continue;
    if (anchor.charStart <= segment.chars[0]) return offset;
    const end = Math.min(anchor.charStart, segment.chars[1]);
    offset += Math.max(0, end - segment.chars[0]);
    if (anchor.charStart <= segment.chars[1]) return offset;
  }
  return offset;
}

export function docPackAnchorForPageReadableCharOffset(
  page: DocPackPage,
  readableCharOffset: number,
  classOverrides?: DocPackReadingClassOverrides,
): DocPackReadingAnchor | undefined {
  let remaining = Math.max(0, readableCharOffset);
  let lastEnabledEnd: number | undefined;

  for (const segment of page.segments) {
    if (!isDocPackClassEnabled(segment, classOverrides)) continue;
    const length = segment.chars[1] - segment.chars[0];
    lastEnabledEnd = segment.chars[1];
    if (remaining <= length) {
      return {
        page: page.page,
        charStart: segment.chars[0] + remaining,
      };
    }
    remaining -= length;
  }

  return lastEnabledEnd === undefined
    ? undefined
    : { page: page.page, charStart: lastEnabledEnd };
}

/**
 * Locate a document position in READABLE space: `readableCharOffset` counts
 * only enabled-class chars and is NOT an absolute offset into the page text —
 * it must never be used as `DocPackReadingAnchor.charStart`. Convert with
 * docPackAnchorForPageReadableCharOffset once the page is loaded (the page's
 * segment layout is required to map readable → absolute).
 */
export function docPackReadablePageOffsetForDocumentPositionMs(
  catalog: Pick<DocPackCatalog, 'pages'>,
  positionMs: number,
  classOverrides?: DocPackReadingClassOverrides,
  estimatedWordMs = DEFAULT_ESTIMATED_WORD_MS,
): { page: number; readableCharOffset: number } | undefined {
  let elapsed = 0;
  let lastReadablePage:
    | {
        page: number;
        chars: number;
      }
    | undefined;
  for (const page of catalog.pages) {
    const counts = docPackCatalogPageReadableCounts(page, classOverrides);
    const pageDuration = counts.words * estimatedWordMs;
    if (counts.words > 0 && counts.chars > 0) {
      lastReadablePage = { page: page.n, chars: counts.chars };
    }
    if (pageDuration <= 0) continue;
    const nextElapsed = elapsed + pageDuration;
    if (positionMs <= nextElapsed) {
      const ratio = Math.min(
        1,
        Math.max(0, (positionMs - elapsed) / pageDuration),
      );
      return {
        page: page.n,
        readableCharOffset: Math.round(counts.chars * ratio),
      };
    }
    elapsed = nextElapsed;
  }

  if (!lastReadablePage) return;
  return {
    page: lastReadablePage.page,
    readableCharOffset: lastReadablePage.chars,
  };
}
